import { type Breakpoint } from '@divi/types';

import {
  type MultiViewDataItemClassName,
  type MultiViewDataUpdateClassName,
  type MultiViewUpdateElementParams,
} from '../../types';
import { addEventListenerHover } from '../add-event-listener-hover';
import { dispatchCustomEvent } from '../dispatch-custom-event';
import { parseData } from '../parse-data';

/**
 * Sets the class of an element and dispatches a custom event.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to set the class of an element.
 * @param {MultiViewDataItemClassName} params.stateData The data to set as the class.
 * @param {MultiViewDataUpdateClassName} params.item The data item being updated.
 * @param {HTMLElement} params.element The element to update.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint.
 * @param {string} params.state The state of the element ('default' or 'hover').
 * @param {MouseEvent | TouchEvent} params.originalEvent The original event that triggered the update.
 */
const setElementClassNames = ({
  stateData,
  item,
  element,
  breakpoint,
  state,
  originalEvent,
}: MultiViewUpdateElementParams<MultiViewDataItemClassName>) => {
  let updatedData: MultiViewDataItemClassName;
  let updated = false;

  stateData?.add?.forEach(key => {
    if (!element.classList.contains(key)) {
      element.classList.add(key);

      updated = true;

      updatedData = {
        ...updatedData,
        add: [...(updatedData?.add ?? [])].concat(key),
      };
    }
  });

  stateData?.remove?.forEach(key => {
    if (element.classList.contains(key)) {
      element.classList.remove(key);

      updated = true;

      updatedData = {
        ...updatedData,
        remove: [...(updatedData?.remove ?? [])].concat(key),
      };
    }
  });

  if (updated) {
    // Dispatch custom event: divi:multiView:setClassName
    dispatchCustomEvent<MultiViewDataItemClassName>({
      breakpoint,
      item,
      originalEvent,
      state,
      updatedData,
    });
  }
};

/**
 * Update element class name.
 *
 * @since ??
 *
 * @param {object} params The parameters needed to update the class name of an element.
 * @param {MultiViewDataUpdateClassName} params.item The item that will be updated.
 * @param {Breakpoint.Name} params.breakpoint The current breakpoint, which can be 'phone', 'tablet', or 'desktop'.
 * @param {boolean} params.isInit Whether this is the first time the function is being called.
 * @param {Breakpoint.Name} params.baseBreakpoint The base breakpoint.
 * @param {Breakpoint.Names} params.breakpointNames The list of breakpoint names.
 */
export const updateDataClassName = ({
  item,
  breakpoint,
  isInit = false,
  baseBreakpoint = 'desktop',
  breakpointNames = ['desktop', 'tablet', 'phone'],
}: {
  item: MultiViewDataUpdateClassName;
  breakpoint: Breakpoint.Name;
  isInit: boolean;
  baseBreakpoint: Breakpoint.Name;
  breakpointNames: Breakpoint.Names;
}): void => {
  const { element, hoverElements, data } = item;
  const { defaultStateData, hoverStateData } = parseData<MultiViewDataItemClassName>({
    data,
    breakpoint,
    baseBreakpoint,
    breakpointNames,
  });

  // Bail early default state data is null.
  // This can happen if the data is not valid.
  if (null === defaultStateData) {
    return;
  }

  const switchDataToDefaultState = (event?: MouseEvent | TouchEvent) => {
    setElementClassNames({
      stateData: defaultStateData,
      state: 'default',
      element,
      item,
      breakpoint,
      originalEvent: event,
    });
  };

  // Handle hover state.
  if (null !== hoverStateData) {
    const switchDataToHoverState = (event: MouseEvent | TouchEvent) => {
      setElementClassNames({
        stateData: hoverStateData,
        state: 'hover',
        element,
        item,
        breakpoint,
        originalEvent: event,
      });
    };

    hoverElements.forEach(hoverElement => {
      addEventListenerHover(
        element,
        hoverElement,
        switchDataToDefaultState,
        switchDataToHoverState,
        'update-data-class-name',
      );
    });
  }

  // Switch to default state on page load or when the breakpoint changes that triggered when the user
  // resizing the browser or changing the device orientation.
  if ('desktop' === breakpoint && isInit && !item.switchOnLoad) {
    return;
  }

  switchDataToDefaultState();
};
